import { chatClient } from '../lib/client';
import { TENANTS_ENDPOINTS } from './tenants.utils';
import type { ApiResponse } from '../lib/types';
import type {
    Tenant,
    TenantCreateRequest,
    TenantCreateResponse,
    TenantUpdateRequest,
    TenantResponse,
    TenantUpdateResponse,
    TenantsListResponse,
    OnboardingResponse,
    Agent,
    ThirdPartySettings,
    ThirdPartySettingsResponse,
    ThirdPartySettingsUpdateRequest,
    WhatsAppTemplate,
    WhatsAppTemplateListResponse,
    WhatsAppTemplateResponse,
    WhatsAppTemplateCreateRequest,
    WhatsAppTemplateDeleteResponse
} from './tenants.types';

/**
 * List all tenants for the authenticated organization
 */
export const listTenants = async (): Promise<ApiResponse<Tenant[]>> => {
    try {
        const response = await chatClient.get<TenantsListResponse>(TENANTS_ENDPOINTS.LIST);
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch tenants';
        return { error: errorMessage };
    }
};

/**
 * Create a new tenant
 */
export const createTenant = async (payload: TenantCreateRequest): Promise<ApiResponse<TenantCreateResponse>> => {
    try {
        const response = await chatClient.post(TENANTS_ENDPOINTS.CREATE, payload);
        return { data: response.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to create tenant';
        return { error: errorMessage };
    }
};

/**
 * Get tenant by MongoDB ObjectId
 */
export const getTenantById = async (id: string): Promise<ApiResponse<Tenant>> => {
    try {
        const response = await chatClient.get<TenantResponse>(TENANTS_ENDPOINTS.GET_BY_ID(id));
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch tenant';
        return { error: errorMessage };
    }
};

/**
 * Update tenant by MongoDB ObjectId (only meta field is allowed for updates)
 */
export const updateTenantById = async (id: string, payload: TenantUpdateRequest): Promise<ApiResponse<Tenant>> => {
    try {
        const response = await chatClient.patch<TenantUpdateResponse>(
            TENANTS_ENDPOINTS.UPDATE_BY_ID(id), 
            payload
        );
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to update tenant';
        return { error: errorMessage };
    }
};

/**
 * Get WhatsApp onboarding URLs for a tenant
 */
export const getWhatsAppOnboarding = async (id: string): Promise<ApiResponse<OnboardingResponse>> => {
    try {
        const response = await chatClient.get<OnboardingResponse>(TENANTS_ENDPOINTS.WHATSAPP_ONBOARDING(id));
        return { data: response.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to get WhatsApp onboarding data';
        return { error: errorMessage };
    }
};

/**
 * Get Facebook onboarding URLs for a tenant
 */
export const getFacebookOnboarding = async (id: string): Promise<ApiResponse<OnboardingResponse>> => {
    try {
        const response = await chatClient.get<OnboardingResponse>(TENANTS_ENDPOINTS.FACEBOOK_ONBOARDING(id));
        return { data: response.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to get Facebook onboarding data';
        return { error: errorMessage };
    }
};

/**
 * Get agents for tenant (includes WhatsApp connection status)
 */
export const getTenantAgents = async (id: string): Promise<ApiResponse<Agent[]>> => {
    try {
        const response = await chatClient.get(TENANTS_ENDPOINTS.GET_AGENTS(id));
        return { data: response.data.data || response.data }; // Handle both wrapped and direct responses
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch agents';
        return { error: errorMessage };
    }
};

/**
 * List all agents with proper internal IDs (uses main /agents endpoint)
 */
export const listAgents = async (): Promise<ApiResponse<Agent[]>> => {
    try {
        const response = await chatClient.get(TENANTS_ENDPOINTS.LIST_AGENTS);
        return { data: response.data.data || response.data }; // Handle both wrapped and direct responses
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch agents';
        return { error: errorMessage };
    }
};

// =============================================================================
// THIRD PARTY SETTINGS ENDPOINTS
// =============================================================================

/**
 * Get currently active third party settings
 */
export const getThirdPartySettings = async (): Promise<ApiResponse<ThirdPartySettings>> => {
    try {
        const response = await chatClient.get<ThirdPartySettingsResponse>(TENANTS_ENDPOINTS.GET_THIRD_PARTY_SETTINGS);
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch third party settings';
        return { error: errorMessage };
    }
};

/**
 * Update third party settings
 */
export const updateThirdPartySettings = async (payload: ThirdPartySettingsUpdateRequest): Promise<ApiResponse<ThirdPartySettings>> => {
    try {
        const response = await chatClient.patch<ThirdPartySettingsResponse>(
            TENANTS_ENDPOINTS.UPDATE_THIRD_PARTY_SETTINGS,
            payload
        );
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to update third party settings';
        return { error: errorMessage };
    }
};

// =============================================================================
// WHATSAPP TEMPLATES ENDPOINTS
// =============================================================================

/**
 * List WhatsApp templates with optional group filtering
 */
export const listWhatsAppTemplates = async (group?: string): Promise<ApiResponse<WhatsAppTemplate[]>> => {
    try {
        const url = group 
            ? `${TENANTS_ENDPOINTS.LIST_WHATSAPP_TEMPLATES}?group=${encodeURIComponent(group)}`
            : TENANTS_ENDPOINTS.LIST_WHATSAPP_TEMPLATES;
        const response = await chatClient.get<WhatsAppTemplateListResponse>(url);
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch WhatsApp templates';
        return { error: errorMessage };
    }
};

/**
 * Submit WhatsApp template for approval
 */
export const createWhatsAppTemplate = async (payload: WhatsAppTemplateCreateRequest): Promise<ApiResponse<WhatsAppTemplate>> => {
    try {
        const response = await chatClient.post<WhatsAppTemplateResponse>(
            TENANTS_ENDPOINTS.CREATE_WHATSAPP_TEMPLATE,
            payload
        );
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to create WhatsApp template';
        return { error: errorMessage };
    }
};

/**
 * Get WhatsApp template by ID
 */
export const getWhatsAppTemplateById = async (id: number): Promise<ApiResponse<WhatsAppTemplate>> => {
    try {
        const response = await chatClient.get<WhatsAppTemplateResponse>(TENANTS_ENDPOINTS.GET_WHATSAPP_TEMPLATE(id));
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to fetch WhatsApp template';
        return { error: errorMessage };
    }
};

/**
 * Delete WhatsApp template by ID
 * Note: All languages will be deleted. Actual deletion takes 4 weeks.
 */
export const deleteWhatsAppTemplate = async (id: number): Promise<ApiResponse<object[]>> => {
    try {
        const response = await chatClient.delete<WhatsAppTemplateDeleteResponse>(TENANTS_ENDPOINTS.DELETE_WHATSAPP_TEMPLATE(id));
        return { data: response.data.data };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error && 'response' in error && 
            typeof error.response === 'object' && error.response !== null &&
            'data' in error.response && typeof error.response.data === 'object' &&
            error.response.data !== null && 'message' in error.response.data
            ? String(error.response.data.message)
            : 'Failed to delete WhatsApp template';
        return { error: errorMessage };
    }
}; 